import React, { useMemo, useState } from 'react';
import Button from '../../ui/Button/Button';
import IconButton from '../../ui/IconButton';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { Input } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { toast } from '../../ui/feedback';
import {
    partsOf, withReturned, partsFromItems, fifoPreview, partKeyOf, partKeyText, partQtyOf, isQtyLockedPart,
    unitAbbrOf, lineCostOf, amountOf, invoiceDayOf, dubaiDayOf, dayText, moneyText,
} from '../../services/serviceRecords.logic';

// The service form's parts on the new store (owner, 28 Sep 2026: FIFO per part). The employee picks a PART and a
// quantity - never an invoice: the engine takes the quantity from the part's invoices oldest first and costs each
// line at its own invoice's price. The preview below shows what the engine will save from the stock as loaded; the
// engine's saved answer is the truth (the page shows it after the save).

const sum3 = (a, b) => Math.round((a + b) * 1000) / 1000;
const keyText = (name, partNumber) => partKeyText(partKeyOf(name, partNumber));
const label = (name, partNumber) => (partNumber ? `${name} (${partNumber})` : name);
const qtyText = (q, unit) => `${q}${unit ? ` ${unitAbbrOf(unit)}` : ''}`;

/** One preview line as the table shows it: the line's own fields first, then its batch's (a legacy line). */
const lineRow = (line, byId) => {
    const batch = byId.get(String(line?.itemId ?? ''));
    const own = line && typeof line === 'object' && 'name' in line;
    const name = (own ? String(line.name || '').trim() : String(batch?.name || '').trim()) || 'Unnamed Item';
    const partNumber = own ? String(line.partNumber || '').trim() : String(batch?.partNumber || '').trim();
    const invoiceDay = invoiceDayOf(own ? line.invoiceDate : batch?.invoiceDate);
    const entered = batch ? dubaiDayOf(batch.created_at) : null;
    const qty = amountOf(line?.quantity);
    const unitCost = amountOf(line?.unitCost);
    return {
        part: batch || own ? label(name, partNumber) : `Unknown part (${line?.itemId ?? '?'})`,
        invoice: String(line?.invoiceNumber || '').trim() || '—',
        date: invoiceDay ? dayText(invoiceDay) : (entered ? `— (entered ${dayText(entered)})` : '—'),
        qty: qty === null ? '—' : qtyText(qty, own ? line.unit : batch?.unit),
        unitCost: unitCost === null ? '—' : unitCost.toFixed(2),
        cost: qty === null || unitCost === null ? '—' : moneyText(lineCostOf(qty, unitCost)),
    };
};

export default function FifoPartsSection({ inventory = [], parts = [], onChange, savedItems = [] }) {
    const [search, setSearch] = useState('');
    const [showList, setShowList] = useState(false);
    const [picked, setPicked] = useState(null);
    const [qty, setQty] = useState('');

    // on an edit the record's own parts go back first, so they count as available (engine _take_parts)
    const stock = useMemo(() => partsOf(withReturned(inventory, savedItems)).filter((p) => p.available > 0), [inventory, savedItems]);
    const byId = useMemo(() => new Map((inventory || []).filter(Boolean).map((b) => [String(b.id), b])), [inventory]);
    const unknown = useMemo(() => partsFromItems(savedItems, inventory).unknown, [savedItems, inventory]);
    const preview = useMemo(() => fifoPreview(inventory, parts, savedItems), [inventory, parts, savedItems]);

    const matches = stock.filter((p) => {
        const s = search.trim().toLowerCase();
        return p.name.toLowerCase().includes(s) || p.partNumber.toLowerCase().includes(s);
    }).slice(0, 8);
    const locked = isQtyLockedPart(picked);

    const pick = (part) => {
        setPicked(part);
        setSearch(label(part.name, part.partNumber));
        setShowList(false);
        setQty(isQtyLockedPart(part) ? '1' : '');
    };

    const add = () => {
        if (!picked) return;
        const q = partQtyOf(locked ? 1 : qty);
        if (q === null) {
            toast.error('Enter a quantity above 0 with at most 3 decimals.');
            return;
        }
        const k = partKeyText(picked.key);
        const already = parts.filter((p) => keyText(p.name, p.partNumber) === k).reduce((s, p) => sum3(s, partQtyOf(p.quantity) || 0), 0);
        if (sum3(already, q) > picked.available) {
            toast.error(`Only ${qtyText(picked.available, picked.unit)} of ${picked.name} in stock`);
            return;
        }
        // the same part added twice is one part: its quantities are summed (the engine sums them too)
        const next = already > 0
            ? parts.map((p) => (keyText(p.name, p.partNumber) === k ? { ...p, quantity: sum3(partQtyOf(p.quantity) || 0, q) } : p))
            : [...parts, { name: picked.name, partNumber: picked.partNumber, unit: picked.unit, quantity: q }];
        onChange(next);
        setPicked(null);
        setSearch('');
        setQty('');
    };

    const remove = (index) => onChange(parts.filter((_, i) => i !== index));

    const shortBy = new Map(preview.short.map((s) => [partKeyText(s.key), s]));
    const problemBy = new Map(preview.problems.map((p) => [partKeyText(p.key), p]));
    const hasRows = preview.items.length > 0 || preview.short.length > 0 || preview.problems.length > 0;

    return (
        <>
            <section className="items-section" aria-labelledby="fsh-items-title">
                <h4 id="fsh-items-title">Parts Used</h4>

                <div className="add-item-row">
                    <div className="item-search-container">
                        <Input
                            type="text"
                            aria-label="Search part by name or PN"
                            autoComplete="off"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setShowList(true);
                                setPicked(null);
                            }}
                            onFocus={() => setShowList(true)}
                            placeholder="Search part by name or PN..."
                        />
                        {showList && search && (
                            <div className="item-dropdown">
                                {matches.length === 0 && <p className="items-empty fifo-no-match">No part in stock matches.</p>}
                                {matches.map((part) => (
                                    <button type="button" key={partKeyText(part.key)} className="item-option" onClick={() => pick(part)}>
                                        <span className="item-option-main">
                                            <strong>{part.name}</strong>
                                            {part.partNumber && <span className="item-option-pn">{part.partNumber}</span>}
                                        </span>
                                        <span className="item-option-details">
                                            Available: {qtyText(part.available, part.unit)}
                                            {part.next ? ` | Next: ${amountOf(part.next.unitCost)?.toFixed(2) ?? '—'}${part.next.invoiceNumber ? ` (${part.next.invoiceNumber})` : ''}` : ''}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <Input
                        type="number"
                        aria-label="Quantity"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                        placeholder="Qty"
                        className="quantity-input"
                        step="0.1"
                        disabled={locked}
                    />

                    <Button type="button" onClick={add} disabled={!picked} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                        Add
                    </Button>
                </div>

                {parts.length === 0 && unknown.length === 0 ? (
                    <p className="items-empty">No parts added yet.</p>
                ) : (
                    <ul className="items-list">
                        {parts.map((p, index) => {
                            const k = keyText(p.name, p.partNumber);
                            const short = shortBy.get(k);
                            return (
                                <li key={k} className="item-row">
                                    <span className="item-row__name">
                                        {p.name}{' '}
                                        {p.partNumber
                                            ? <span className="item-row__pn">({p.partNumber})</span>
                                            : <span className="item-row__missing">(Empty PN)</span>}
                                    </span>
                                    <span className="item-row__qty">({qtyText(p.quantity, p.unit)})</span>
                                    {short && <span className="fifo-short">Not enough in stock: {qtyText(short.available, short.unit)} available</span>}
                                    <IconButton
                                        icon={ICONS.close}
                                        label={`Remove ${p.name}`}
                                        size="xs"
                                        tone="danger"
                                        onClick={() => remove(index)}
                                    />
                                </li>
                            );
                        })}
                        {unknown.map((line, i) => (
                            <li key={`unknown-${i}`} className="item-row">
                                <span className="item-row__name">Unknown part ({String(line?.itemId ?? '?')}) - kept as saved</span>
                                <span className="item-row__qty">({line?.quantity})</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {hasRows && (
                <section className="fifo-preview" aria-labelledby="fsh-fifo-title">
                    <h4 id="fsh-fifo-title">Cost preview (oldest invoice first)</h4>
                    <TableContainer maxHeight="260px">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>Part</TableHeader>
                                    <TableHeader>Invoice No.</TableHeader>
                                    <TableHeader>Invoice Date</TableHeader>
                                    <TableHeader>Qty</TableHeader>
                                    <TableHeader>Unit Cost</TableHeader>
                                    <TableHeader>Cost</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {preview.items.map((line, i) => {
                                    const r = lineRow(line, byId);
                                    return (
                                        <TableRow key={`${line?.itemId ?? 'line'}-${i}`}>
                                            <TableCell>{r.part}</TableCell>
                                            <TableCell>{r.invoice}</TableCell>
                                            <TableCell>{r.date}</TableCell>
                                            <TableCell>{r.qty}</TableCell>
                                            <TableCell>{r.unitCost}</TableCell>
                                            <TableCell>{r.cost}</TableCell>
                                        </TableRow>
                                    );
                                })}
                                {preview.short.map((s) => (
                                    <TableRow key={`short-${partKeyText(s.key)}`} className="fifo-short-row">
                                        <TableCell>{label(s.name, s.partNumber)}</TableCell>
                                        <TableCell colSpan={5}>
                                            Not enough in stock: {qtyText(s.available, s.unit)} available, {qtyText(s.asked, s.unit)} asked
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {[...problemBy.values()].map((p) => (
                                    <TableRow key={`problem-${partKeyText(p.key)}`} className="fifo-short-row">
                                        <TableCell colSpan={6}>{p.message}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow className="fifo-total-row">
                                    <TableCell colSpan={5}>Total</TableCell>
                                    <TableCell>{preview.total === null ? '—' : `${moneyText(preview.total)} AED`}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <p className="fifo-preview__note">
                        From the stock as loaded - the saved cost is the engine&apos;s (it may differ if stock changed).
                    </p>
                </section>
            )}
        </>
    );
}
